import axios from "../../axios/AxiosInstance";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, KeyRound, Loader2, Mail } from "lucide-react";
import logo from "../../assets/img/jm-interim-transparent.png";

export default function ForgotPassword() {
  const [username, setUsername] = useState("");
  const [askedPassword, setAskedPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const askPassword = async (event) => {
    event.preventDefault();
    if (loading) return;
    const login = username.trim();
    if (!login) {
      setError("Veuillez renseigner votre identifiant.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post("/auth/ask-reset-password-freelance", { username: login });
      setAskedPassword(true);
    } catch (e) {
      setError(e.response?.status === 404
        ? "Cet identifiant est introuvable. Vérifiez votre saisie."
        : "L’envoi n’a pas abouti. Vérifiez votre identifiant et réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-slate-50 text-slate-900">
      <header className="flex items-center justify-between gap-4 px-5 py-5 sm:px-10">
        <Link to="/" aria-label="Accueil J’M Intérim">
          <img src={logo} alt="J’M Intérim" className="h-16 w-28 rounded-lg object-contain" />
        </Link>
        <Link to="/login" className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-white hover:text-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
          <ArrowLeft size={16} aria-hidden="true" /> Connexion
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-8">
        <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-950/5 lg:grid-cols-2">
          <aside className="relative overflow-hidden bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 px-8 py-10 text-white sm:p-12 lg:flex lg:flex-col lg:justify-between">
            <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border-[48px] border-white/5" />
            <div className="relative">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Votre espace recruteur</span>
              <h2 className="mt-6 max-w-sm text-3xl font-semibold leading-tight sm:text-4xl">Un nouveau départ,<br />en quelques clics.</h2>
              <p className="mt-5 max-w-sm text-base leading-7 text-blue-100">Retrouvez l’accès à votre espace et reprenez le fil de vos recrutements.</p>
            </div>
            <div className="relative mt-12 hidden border-t border-white/15 pt-6 lg:block">
              <span className="mb-3 block h-1 w-10 rounded-full bg-pink-500" />
              <p className="text-sm text-blue-100">L’humain au cœur de nos missions.</p>
            </div>
          </aside>

          <section className="px-6 py-10 sm:p-12" aria-labelledby="reset-title">
            {askedPassword ? (
              <div role="status" aria-live="polite">
                <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><Check size={28} aria-hidden="true" /></div>
                <h1 id="reset-title" className="text-3xl font-semibold tracking-tight">Consultez votre boîte mail</h1>
                <p className="mt-4 text-sm leading-7 text-slate-500">Un lien de réinitialisation vous a été envoyé. Suivez les instructions du message pour choisir votre nouveau mot de passe.</p>
                <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-blue-900">Vous ne trouvez pas le message ? Pensez à vérifier vos courriers indésirables.</div>
                <Link to="/login" className="mt-8 flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-800">Retour à la connexion <ArrowRight size={18} aria-hidden="true" /></Link>
              </div>
            ) : (
              <>
                <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-900"><KeyRound size={26} aria-hidden="true" /></div>
                <h1 id="reset-title" className="text-3xl font-semibold tracking-tight">Mot de passe oublié ?</h1>
                <p className="mt-4 text-sm leading-7 text-slate-500">Renseignez votre identifiant de connexion. Nous vous enverrons un lien pour créer un nouveau mot de passe.</p>
                <form onSubmit={askPassword} className="mt-8">
                  <label htmlFor="reset-username" className="mb-2 block text-sm font-semibold text-slate-700">Votre identifiant</label>
                  <div className="relative">
                    <Mail size={19} aria-hidden="true" className="pointer-events-none absolute left-4 top-4 text-slate-400" />
                    <input id="reset-username" name="username" autoComplete="username" required value={username} onChange={(event) => { setUsername(event.target.value); setError(""); }} disabled={loading} placeholder="Votre identifiant de connexion" aria-invalid={Boolean(error)} aria-describedby={error ? "reset-error" : undefined} className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:opacity-60" />
                  </div>
                  {error && <p id="reset-error" role="alert" className="mt-3 text-sm text-rose-600">{error}</p>}
                  <button type="submit" disabled={loading} className="mt-5 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-wait disabled:opacity-70">
                    {loading ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Envoi en cours…</> : <>Envoyer le lien <ArrowRight size={18} aria-hidden="true" /></>}
                  </button>
                </form>
                <Link to="/login" className="mt-7 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 transition hover:text-blue-900"><ArrowLeft size={16} aria-hidden="true" /> Retour à la connexion</Link>
              </>
            )}
          </section>
        </div>
      </main>
      <footer className="px-4 py-6 text-center text-xs text-slate-400">J’M Intérim · Votre partenaire recrutement</footer>
    </div>
  );
}
